import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';
import { ApiFeil } from '../api/klient';
import { konsollApi, type Ansatt } from '../api/konsoll';
import ReiseDetalj from '../components/konsoll/ReiseDetalj';
import ReiseListe from '../components/konsoll/ReiseListe';

/** Varsler på e-post til den innloggede ansatte (T114). Vises ikke hvis de ikke kan hentes. */
function VarselBryter() {
  const { t } = useTranslation();
  const [pa, setPa] = useState<boolean | null>(null);

  useEffect(() => {
    konsollApi
      .varsler()
      .then((v) => setPa(v.pa))
      .catch(() => setPa(null));
  }, []);

  if (pa === null) return null;
  return (
    <label className="ml-2 flex items-center gap-1.5">
      <input
        type="checkbox"
        checked={pa}
        onChange={(e) => {
          const ny = e.target.checked;
          setPa(ny);
          konsollApi.settVarsler(ny).catch(() => setPa(!ny));
        }}
        className="h-4 w-4 rounded border-slate-300 text-aksent-600 focus:ring-aksent-600"
      />
      {t('konsoll.varsler')}
    </label>
  );
}

/** /konsoll. Arbeidsbordet for de ansatte: reisene, og hver reise i detalj. Krever innlogging. */
export default function Konsoll() {
  const { t } = useTranslation();
  const [ansatt, setAnsatt] = useState<Ansatt | null | undefined>(undefined);

  useEffect(() => {
    konsollApi
      .meg()
      .then(setAnsatt)
      .catch(() => setAnsatt(null));
  }, []);

  async function loggUt() {
    await konsollApi.loggUt().catch(() => undefined);
    setAnsatt(null);
  }

  return (
    <section>
      <div className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="etikett mb-2">{t('app.navn')}</p>
          <h1 className="text-[2rem] font-semibold leading-tight">{t('konsoll.tittel')}</h1>
        </div>
        {ansatt && (
          <div className="flex items-center gap-1 text-sm text-slate-500">
            <span>{t('konsoll.innlogging.innloggetSom', { epost: ansatt.epost })}</span>
            <VarselBryter />
            <button type="button" onClick={loggUt} className="knapp-stille min-h-0 px-2 py-1 text-sm">
              {t('konsoll.innlogging.loggUt')}
            </button>
          </div>
        )}
      </div>

      {ansatt === undefined && <p role="status">{t('konsoll.laster')}</p>}
      {ansatt === null && <Innlogging onInnlogget={setAnsatt} />}
      {ansatt && (
        <Routes>
          <Route index element={<ReiseListe />} />
          <Route path="reiser/:id" element={<ReiseDetalj ansatt={ansatt} />} />
        </Routes>
      )}
    </section>
  );
}

function Innlogging({ onInnlogget }: { onInnlogget: (ansatt: Ansatt) => void }) {
  const { t } = useTranslation();
  const [epost, setEpost] = useState('');
  const [passord, setPassord] = useState('');
  const [feil, setFeil] = useState<string | null>(null);
  const [opptatt, setOpptatt] = useState(false);

  async function loggInn(event: FormEvent) {
    event.preventDefault();
    setOpptatt(true);
    setFeil(null);
    try {
      onInnlogget(await konsollApi.loggInn(epost.trim(), passord));
    } catch (e) {
      setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
    } finally {
      setOpptatt(false);
    }
  }

  return (
    <form onSubmit={loggInn} className="kort max-w-md p-8 sm:p-10">
      <h2 className="mb-6 text-xl font-semibold">{t('konsoll.innlogging.tittel')}</h2>
      <label htmlFor="konsoll-epost" className="mb-1.5 block text-sm font-medium text-slate-700">
        {t('konsoll.innlogging.epost')}
      </label>
      <input
        id="konsoll-epost"
        type="email"
        autoComplete="username"
        value={epost}
        onChange={(e) => setEpost(e.target.value)}
        className="felt mb-4 w-full"
      />
      <label htmlFor="konsoll-passord" className="mb-1.5 block text-sm font-medium text-slate-700">
        {t('konsoll.innlogging.passord')}
      </label>
      <input
        id="konsoll-passord"
        type="password"
        autoComplete="current-password"
        value={passord}
        onChange={(e) => setPassord(e.target.value)}
        className="felt mb-4 w-full"
      />
      {feil && (
        <p role="alert" className="mb-4 melding-feil">
          {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
        </p>
      )}
      <button type="submit" disabled={opptatt || !epost || !passord} className="knapp-primaer w-full">
        {t('konsoll.innlogging.loggInn')}
      </button>
    </form>
  );
}
